<template>
  <div class="dropdown-parent-container">
    <button
      type="button"
      title="ChartList View Options"
      class="dropdown-menu-toggle-arrow-icon-button sc3-button"
      :class="[
        dropdownMenuToggleButtonCssColorSc3ButtonClass,
        {
          'dropdown-menu-toggle-arrow-icon-button-open sc3-button-active': isOpen,
          'sc3-button-inline': isInline,
          'sc3-button-fixed-height': isFixedHeight
        }
      ]"
      ref="viewListAsDropdownMenuButton"
      @click="toggleDropdown()"
      :disabled="isDisabled"
    >
      <i class="fas fa-fw fa-eye"></i>
      <span class="medium-desktop-hidden">&nbsp;View As</span>
      &nbsp;
      <i class="fas fa-angle-down"></i>
    </button>
    <DropdownMenu
      :nodes-excluded-from-closing-on-outside-click="viewListAsDropdownMenuButton ? [viewListAsDropdownMenuButton] : []"
      :dropdown-button-left-value="left"
      v-model:is-open="isOpen"
      v-model:max-body-height="dropdownHeight"
      :dropdown-menu-toggle-button-node="viewListAsDropdownMenuButton"
    >
      <template v-slot:body>
        <ul>
          <template
            v-for="viewOption in VIEW_LIST_AS_OPTIONS"
            :key="viewOption.value"
          >
            <li :class="{ 'disabled': isLoggedIn && chartList?.isReadonly && viewOption.shouldDisableForReadonlyChartLists }">
              <button
                v-if="!isLoggedIn"
                type="button"
                @click="[closeDropdown(), launchCreateAccountModal()]"
              >{{ viewOption.title }}</button>
              <a
                v-else-if="!chartList"
                href="/panels"
                target="_blank"
                @click="closeDropdown()"
              >{{ viewOption.title }}</a>
              <a
                v-else
                :href="`/panels/?cmd=show&listNum=${chartList.listNum}&viewType=${viewOption.type}${ typeof selectedChart?.recid === 'number' ? `&recID=${selectedChart.recid}` : '' }`"
                target="_blank"
                @click="closeDropdown()"
                :disabled="chartList.isReadonly && viewOption.shouldDisableForReadonlyChartLists"
              >{{ viewOption.title }}</a>
            </li>
          </template>
        </ul>
      </template>
    </DropdownMenu>
  </div>
</template>

<script lang="ts" setup>
  import { ref, toRefs, watch } from 'vue';
  import { useAuthentication } from '@/composables/useAuthentication';
  import { useBoundingClientRect } from '@/composables/useBoundingClientRect';
  import { useModals } from '@/composables/useModals';
  import DropdownMenu from '@/components/dropdowns/DropdownMenu.vue';
  import { ViewListAsSelection } from '@/enums/view-list-as-selections';
  import { ChartList } from '@/interfaces/chartList';
  import { SimpleFrontendChart } from '@/interfaces/chart';

  interface ViewSelection {
    title: string;
    type: ViewListAsSelection;
    shouldDisableForReadonlyChartLists: boolean;
  };

  const VIEW_LIST_AS_OPTIONS: ViewSelection[] = [
    { title: 'Summary', type: ViewListAsSelection.Summary, shouldDisableForReadonlyChartLists: false },
    { title: 'ChartList View', type: ViewListAsSelection.ChartListView, shouldDisableForReadonlyChartLists: false },
    { title: 'ChartBook', type: ViewListAsSelection.ChartBook, shouldDisableForReadonlyChartLists: false },
    { title: 'GalleryView', type: ViewListAsSelection.GalleryView, shouldDisableForReadonlyChartLists: false },
    { title: 'CandleGlance', type: ViewListAsSelection.CandleGlance, shouldDisableForReadonlyChartLists: false },
    { title: 'RRG', type: ViewListAsSelection.RRG, shouldDisableForReadonlyChartLists: false },
    { title: 'Symbol Summary', type: ViewListAsSelection.SymbolSummary, shouldDisableForReadonlyChartLists: false },
    { title: 'Performance', type: ViewListAsSelection.Performance, shouldDisableForReadonlyChartLists: false },
    { title: 'Options', type: ViewListAsSelection.Options, shouldDisableForReadonlyChartLists: false },
    { title: 'Seasonality', type: ViewListAsSelection.Seasonality, shouldDisableForReadonlyChartLists: false },
    { title: 'MarketCarpet', type: ViewListAsSelection.MarketCarpet, shouldDisableForReadonlyChartLists: false },
    { title: 'Correlation', type: ViewListAsSelection.Correlation, shouldDisableForReadonlyChartLists: false },
    { title: 'Edit', type: ViewListAsSelection.Edit, shouldDisableForReadonlyChartLists: true }
  ];

  const props = defineProps<{
    chartList: ChartList | null;
    selectedChart?: SimpleFrontendChart;
    isInline?: boolean;
    isFixedHeight?: boolean;
    dropdownMenuToggleButtonCssColorSc3ButtonClass: string;
    isDisabled?: boolean;
  }>();
  const {
    chartList,
    selectedChart,
    isInline,
    isFixedHeight,
    dropdownMenuToggleButtonCssColorSc3ButtonClass,
    isDisabled
  } = toRefs(props);
  const { isLoggedIn } = useAuthentication();
  const { getBoundingClientRectPropertyRefs } = useBoundingClientRect();
  const { launchCreateAccountModal } = useModals();
  const viewListAsDropdownMenuButton = ref<HTMLButtonElement>();
  const isOpen = ref<boolean>(false);
  const dropdownHeight = ref<number>(0);
  const { left } = getBoundingClientRectPropertyRefs(viewListAsDropdownMenuButton, { windowResize: true });

  function toggleDropdown() {
    if (isDisabled.value) {
      return;
    }

    isOpen.value = !isOpen.value;
  }

  function closeDropdown() {
    isOpen.value = false;
  }

  watch(isOpen, () => {
    if (!isOpen.value) {
      viewListAsDropdownMenuButton.value?.blur();
    }
  });
</script>

<style scoped>
  .dropdown-parent-container {
    position: relative;
  }

  ul {
    max-height: v-bind('`${dropdownHeight}px`');
  }
</style>
